import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { agEnterPlaceholder } from './attorneyGeneralPlaceholders';
import { validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_REF = 255;

export default function AttorneyGeneralStep7LegalExtraditionPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const showWarrantRef = draft.arrestWarrantAvailable === 'yes';

  useEffect(() => {
    if (draft.arrestWarrantAvailable === 'yes' || draft.arrestWarrantAvailable === 'no') return;
    setDraft((d) => ({ ...d, arrestWarrantAvailable: 'no' }));
  }, [draft.arrestWarrantAvailable, setDraft]);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(7, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <AttorneyGeneralWizardShell currentStep={7} draft={draft}>
      <SectionCard title={f.sectionLegalExtraditionCaseDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.arrestWarrantAvailable}</label>
          <div className="ccr-radio-row" id="ag-arrestWarrant">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className="ccr-radio">
                <input
                  type="radio"
                  name="ag-arrestWarrant"
                  checked={draft.arrestWarrantAvailable === v}
                  onChange={() => {
                    setDraft((d) => ({
                      ...d,
                      arrestWarrantAvailable: v,
                      ...(v === 'no' ? { warrantReference: '' } : {}) }));
                    clearErr('arrestWarrantAvailable');
                    clearErr('warrantReference');
                  }}
                />
                {v === 'yes' ? f.yes : f.no}
              </label>
            ))}
          </div>
          <FieldError message={errors.arrestWarrantAvailable} />
        </div>

        {showWarrantRef ? (
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>
              {f.warrantReference} <span className="req">*</span>
            </label>
            <input
              id="ag-warrantReference"
              className={inpClass(Boolean(errors.warrantReference))}
              type="text"              value={draft.warrantReference}
              onChange={(e) => {
                setDraft((d) => ({ ...d, warrantReference: maxLen('warrantReference', e.target.value, MAX_REF) }));
              }}
              placeholder={agEnterPlaceholder('Warrant Reference')}
            />
            <FieldError message={errors.warrantReference} />
          </div>
        ) : null}

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.applicableTreatyArrangement}</label>
          <input
            id="ag-treaty"
            className={inpClass(Boolean(errors.applicableTreatyArrangement))}
            type="text"            value={draft.applicableTreatyArrangement}
            onChange={(e) => {
              setDraft((d) => ({ ...d, applicableTreatyArrangement: maxLen('applicableTreatyArrangement', e.target.value, MAX_REF) }));
            }}
            placeholder={f.applicableTreatyArrangement}
          />
          <FieldError message={errors.applicableTreatyArrangement} />
        </div>
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={7}
        onBack={() => nav(attorneyGeneralWizardPath(6, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(8, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
